import * as React from 'react';
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

/**
 * Gallery4 — shadcn-style block (adapted to CRA + JSX).
 * Primitives live in `src/components/ui` (`@/components/ui/*`).
 */

const DEMO_ITEMS = [
  {
    id: 'demo-1',
    title: 'shadcn/ui',
    description:
      'Accessible components you can copy into your app and customize freely.',
    href: 'https://ui.shadcn.com',
    image:
      'https://images.unsplash.com/photo-1551250928-243dc937c49d?auto=format&fit=crop&w=1080&q=80',
  },
  {
    id: 'demo-2',
    title: 'Tailwind CSS',
    description: 'Utility-first styling for rapid UI iteration.',
    href: 'https://tailwindcss.com',
    image:
      'https://images.unsplash.com/photo-1551250928-e4a05afaed1e?auto=format&fit=crop&w=1080&q=80',
  },
];

/**
 * @typedef {{ id: string; title: string; description: string; href: string; image: string; imageObjectPosition?: string }} Gallery4Item
 */

/**
 * @param {{
 *   title?: React.ReactNode;
 *   description?: string;
 *   items?: Gallery4Item[];
 *   className?: string;
 *   readMoreLabel?: string;
 *   defaultImageObjectPosition?: string;
 * }} props
 */
function Gallery4({
  title = 'Case Studies',
  description = 'Discover how leading teams ship exceptional digital experiences.',
  items = DEMO_ITEMS,
  className,
  readMoreLabel = 'Read more',
  defaultImageObjectPosition = 'object-center',
}) {
  const [carouselApi, setCarouselApi] = useState(undefined);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const updateSelection = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };
    updateSelection();
    carouselApi.on('select', updateSelection);
    return () => {
      carouselApi.off('select', updateSelection);
    };
  }, [carouselApi]);

  return (
    <section className={cn('py-16 md:py-24 lg:py-28', className)}>
      <div className="container mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="mb-8 flex max-w-2xl flex-col gap-3 md:mb-14 md:gap-4 lg:mb-16">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {title}
          </h2>
          {description ? (
            <p className="max-w-lg text-muted-foreground md:text-[17px]">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="relative w-full">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: 'start',
            loop: false,
          }}
          className="w-full"
        >
          <CarouselPrevious
            className={cn(
              'left-3 top-[calc(50%-1rem)] z-20 h-11 w-11 -translate-y-1/2 border-white/15 bg-black/55 text-white shadow-lg backdrop-blur-md',
              'hover:bg-black/70 hover:text-[#F5E6A8] disabled:opacity-35 md:left-6',
            )}
          />
          <CarouselNext
            className={cn(
              'right-3 top-[calc(50%-1rem)] z-20 h-11 w-11 -translate-y-1/2 border-white/15 bg-black/55 text-white shadow-lg backdrop-blur-md',
              'hover:bg-black/70 hover:text-[#F5E6A8] disabled:opacity-35 md:right-6',
            )}
          />
          <CarouselContent className="ml-0 px-12 md:px-16 2xl:ml-[max(8rem,calc(50vw-700px))] 2xl:mr-[max(0rem,calc(50vw-700px))]">
            {items.map((item) => (
              <CarouselItem
                key={item.id}
                className="max-w-[320px] pl-[20px] lg:max-w-[360px]"
              >
                <a href={item.href} className="group block h-full rounded-xl">
                  <div className="group relative h-full min-h-[27rem] max-w-full overflow-hidden rounded-xl md:aspect-[5/4] lg:aspect-[16/9]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className={cn(
                        'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]',
                        item.imageObjectPosition || defaultImageObjectPosition,
                      )}
                    />
                    <div
                      className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[32%] bg-gradient-to-b from-black/70 to-transparent"
                      aria-hidden
                    />
                    <div className="absolute inset-0 z-[1] h-full bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 z-[2] flex flex-col items-start p-6 text-white md:p-8">
                      <div className="mb-2 text-xl font-semibold md:mb-3">
                        {item.title}
                      </div>
                      <div className="mb-8 line-clamp-2 text-white/90 md:mb-12 lg:mb-9">
                        {item.description}
                      </div>
                      <div className="flex items-center text-sm font-medium text-white">
                        {readMoreLabel}{' '}
                        <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </a>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="mt-8 flex justify-center gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={cn(
                'h-2 w-2 rounded-full transition-colors',
                currentSlide === index ? 'bg-primary' : 'bg-primary/25',
              )}
              onClick={() => carouselApi?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export { Gallery4, DEMO_ITEMS };
